<template>
  <div class="d-flex align-center">
    <!-- salvia-845: `z-index` explicito porque este componente vive DENTRO do
         diálogo de atendimento. O `v-tooltip` do Vuetify renderiza num elemento
         destacado, com z-index abaixo do overlay do `v-dialog` (~200) — sem isto
         a dica aparece ATRÁS da caixa do atendimento e fica ilegível. -->
    <v-tooltip bottom :z-index="300">
      <template v-slot:activator="{ on, attrs }">
        <div v-bind="attrs" v-on="on" class="mr-2">
          <!--
            salvia-845 -> revisado em 15/09, 21/09 e revertido em 24/09 a
            pedido direto do usuario.

            HISTORICO, para quem achar isto estranho: a 845 trocou o simbolo
            de play pela marca de gravacao (vermelho, redondo). Em 15/09 as
            doutoras reclamaram que o botao JA NASCIA vermelho e parecia estar
            gravando antes de alguem tocar nele — a resposta foi deixa-lo
            verde. Em 21/09 o vermelho voltou, mas com rotulo "Transcrever"
            escrito ao lado: um retangulo antes de gravar, um circulo mudo
            durante.

            Em 24/09 o pedido foi reverter para o circulo vermelho de sempre,
            SEM o retangulo com texto — a versao anterior a toda essa
            discussao. Nao ha ambiguidade de leitura para resolver aqui: e
            decisao direta do usuario, e a unica coisa nova em cima dela e a
            animacao de pulso abaixo.

            CLIQUE, e nao pressionar-e-segurar: aqui a gravacao dura a consulta
            inteira. O modo de segurar existe no DictationButton (salvia-864),
            onde o ditado sao rajadas de segundos e soltar e o fim natural da
            frase. Mesma linguagem visual, interacao diferente porque a duracao e
            outra — segurar por quarenta minutos nao e interacao, e castigo.
          -->
          <v-btn
            fab
            small
            color="error"
            class="btn-rec"
            :class="{ 'btn-rec--pulsando': isRecording && !isPaused }"
            :disabled="isRecording && !isPaused"
            @click="startRecording"
          >
            <v-icon>mdi-record</v-icon>
          </v-btn>
        </div>
      </template>
      <span>{{
        isPaused
          ? "RETOMAR"
          : isRecording
          ? "GRAVANDO"
          : "INICIAR"
      }}</span>
    </v-tooltip>

    <v-tooltip bottom :z-index="300">
      <template v-slot:activator="{ on, attrs }">
        <div v-bind="attrs" v-on="on" class="mr-2">
          <v-btn
            fab
            small
            color="warning"
            :disabled="!isRecording || isPaused"
            @click="pauseRecording"
          >
            <v-icon>mdi-pause</v-icon>
          </v-btn>
        </div>
      </template>
      <span>{{
        !isRecording
          ? "Nenhuma gravação em andamento"
          : isPaused
          ? "Gravação já está pausada"
          : "Pausar gravação"
      }}</span>
    </v-tooltip>

    <!-- Sem rede o reconhecedor para; ele religa sozinho (scheduleReconnect)
         e aqui o médico vê que está reconectando, não gravando. -->
    <div v-if="isRecording && !isPaused && networkError" class="d-flex align-center ml-2">
      <v-progress-circular indeterminate size="14" width="2" color="white" class="mr-2" />
      <span class="text-body-2">RECONECTANDO...</span>
    </div>
    <div v-else-if="isRecording && !isPaused" class="d-flex align-center ml-2">
      <div class="recording-dot mx-1"></div>
      <div class="recording-dot mx-1"></div>
      <div class="recording-dot mx-1"></div>
      <span class="text-body-2 ml-2">GRAVANDO...</span>
    </div>

    <!--
      salvia-860: o que o sistema ouviu como comando.

      Isto nao e enfeite. Gatilho por voz que nao da sinal e PIOR que nao ter
      gatilho: o profissional fala "Salvia, anote X", segue a consulta achando
      que registrou, e so descobre no fim que nao. Mostrar na hora, e deixar
      remover, e o que torna a funcao confiavel antes mesmo de ela fazer algo.

      O "x" existe porque o reconhecedor erra — e porque "Salvia" e o nome do
      produto: se alguem disser "o Salvia travou" no comeco de uma frase, vira
      comando. Descartar tem de custar um clique.
    -->
    <div v-if="voiceCommands.length" class="cv-lista ml-4">
      <div class="cv-titulo">
        <v-icon x-small color="primary" class="mr-1">mdi-microphone-message</v-icon>
        {{ voiceCommands.length }}
        {{ voiceCommands.length === 1 ? "comando ouvido" : "comandos ouvidos" }}
      </div>
      <v-chip
        v-for="(c, i) in voiceCommands"
        :key="`${i}-${c.comando}`"
        x-small
        outlined
        color="primary"
        class="cv-chip"
        close
        @click:close="removeVoiceCommand(i)"
      >
        <span class="ds-chip-label" :title="c.comando">{{ c.comando }}</span>
      </v-chip>
    </div>
  </div>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import { extrairComando } from "@/features/patients/appointments/utils/gatilhoDeVoz";
import {
  appendNoteMarker,
  removeNoteMarker,
} from "@/features/patients/appointments/utils/transcriptLink";

export default {
  name: "AudioRecorder",

  emits: [
    "update:transcription",
    "update:interimTranscript",
    "update:recordingTime",
    "recording-stopped",
    "update:isRecording",
    "recording-paused",
    "update:voiceCommands",
  ],

  props: {
    /**
     * salvia-861: comandos restaurados do armazenamento pelo atendimento.
     * O gravador acumula a partir daqui, em vez de comecar do zero e sobrescrever
     * o que foi recuperado depois de um refresh.
     */
    initialCommands: { type: Array, default: () => [] },

    /**
     * salvia-867: a transcricao que a consulta JA tem.
     *
     * Existe pelo mesmo motivo do `initialCommands` logo acima: o gravador
     * acumula A PARTIR daqui em vez de comecar do zero e sobrescrever o que ja
     * estava na tela.
     *
     * E a prop, nao o `finalTranscript` local, que vale como verdade — porque o
     * medico pode ter EDITADO a transcricao pelo lapis antes de gravar de novo,
     * e continuar a partir da copia local descartaria a edicao dele.
     */
    initialTranscript: { type: String, default: "" },
  },

  setup() {
    const { success, error, warning, info } = useSnackbar();
    return { success, error, warning, info };
  },

  data() {
    return {
      recognition: null,
      speechApiAvailable: false,
      networkError: false,
      recognitionActive: false,
      isRecording: false,
      isPaused: false,
      recordingTime: 0,
      timerInterval: null,
      recordingInterval: null,
      interimTranscript: "",
      finalTranscript: "",
      // salvia-860: comandos ditos com o gatilho "Salvia" durante a consulta.
      // Ficam AQUI, separados da transcricao, ate o fim do atendimento.
      voiceCommands: [...(this.initialCommands || [])],
      lastSpeechTime: null,
      silenceThreshold: 2000,
      // Tentativas seguidas sem ouvir nada (no-speech / audio-capture). Zera a
      // cada fala reconhecida; ao passar do limite, avisa UMA vez.
      silentAttempts: 0,
      silenceWarned: false,
      // Erro de rede do reconhecedor: tentativas seguidas de religar sozinho.
      // Zera a cada fala reconhecida.
      reconnectAttempts: 0,
      reconnectTimer: null,
      // NOVO: Horários de início e fim
      startTime: null,
      endTime: null,
    };
  },

  mounted() {
    this.initSpeechRecognition();
  },

  beforeDestroy() {
    clearTimeout(this.reconnectTimer);
    this.cleanupRecognition();
    clearInterval(this.timerInterval);
    clearInterval(this.recordingInterval);
  },

  watch: {
    isRecording(value) {
      this.$emit("update:isRecording", value);
    },
  },

  methods: {
    /**
     * SALVIA-881: nota escrita durante a gravação entra na transcrição, no
     * ponto atual da conversa. Tem que ser AQUI: enquanto grava, este
     * acumulador é a fonte da verdade e cada fala reemite o texto inteiro — uma
     * nota posta só no diálogo sumiria na frase seguinte.
     *
     * Devolve false quando não está gravando: aí quem guarda o texto é o diálogo.
     */
    insertNote(content) {
      if (!this.isRecording) return false;
      this.finalTranscript = appendNoteMarker(this.finalTranscript, content);
      this.$emit("update:transcription", this.finalTranscript);
      return true;
    },

    /** Desfaz `insertNote` quando a nota é excluída. */
    removeNote(content) {
      if (!this.isRecording) return false;
      this.finalTranscript = removeNoteMarker(this.finalTranscript, content);
      this.$emit("update:transcription", this.finalTranscript);
      return true;
    },

    /**
     * salvia-860/861: reconhece "Salvia" + comando num trecho da fala.
     *
     * O comando NAO sai da transcricao — e isso e o que faz o desenho funcionar.
     * O agente le a conversa INTEIRA para resolver o comando: e assim que "anota
     * o que ela falou sobre o sono" encontra o que ela falou sobre sono, dito
     * minutos antes. Se o comando fosse removido daqui, nao haveria o que
     * procurar.
     *
     * ── DOIS DESTINOS, decidido em 22/09 ────────────────────────────────────
     * Ate aqui tudo virava chip e a proposta so nascia no fim, por clique. O
     * clique saiu: agora o que e PEDIDO DE REGISTRO vira proposta na hora.
     *
     *   ehRegistro = true  -> emite `record-command`; o dialog pede a proposta
     *                         agora e ela aparece nas Notas Salvas, pendente.
     *   ehRegistro = false -> so o chip. E o "nao sei": o gatilho casou, mas pode
     *                         ser alguem falando DO sistema ("o Salvia esta lento
     *                         hoje"). A varredura do fim da consulta decide.
     *
     * O chip continua sendo criado nos DOIS casos, de proposito. Ele e retorno
     * visual — "ouvi voce" — e some-lo para o que nao virou proposta deixaria o
     * profissional sem saber se foi ouvido.
     *
     * Duplicata e ignorada: o reconhecedor as vezes re-emite o mesmo trecho
     * final, e o medico veria o comando duas vezes sem ter falado duas. Isso
     * tambem evita pedir a mesma proposta duas vezes — e o Laravel ainda
     * deduplica por payload, defesa em profundidade.
     */
    captureVoiceCommand(segment) {
      const found = extrairComando(segment);
      if (!found) return;

      const alreadyExists = this.voiceCommands.some(
        (c) => c.comando.toLowerCase() === found.comando.toLowerCase(),
      );
      if (alreadyExists) return;

      this.voiceCommands.push({
        comando: found.comando,
        em: new Date(),
        ehRegistro: found.ehRegistro,
      });
      this.$emit("update:voiceCommands", this.voiceCommands);

      if (found.ehRegistro) {
        this.$emit("record-command", found.comando);
        this.info(`Ouvi: "${found.comando}" — preparando a proposta agora.`);
        return;
      }

      // Nao promete "agora" para o que nao vai virar proposta agora. "Anotado"
      // sugeriria que ja foi tratado, e o profissional so descobriria depois.
      this.info(`Ouvi: "${found.comando}" — vou conferir no fim da consulta.`);
    },

    /** salvia-860: o medico descarta o que o reconhecedor entendeu errado. */
    removeVoiceCommand(index) {
      this.voiceCommands.splice(index, 1);
      this.$emit("update:voiceCommands", this.voiceCommands);
    },

    initSpeechRecognition() {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        this.speechApiAvailable = false;
        this.error("Web Speech API não suportada neste navegador");
        return;
      }

      this.speechApiAvailable = true;
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = "pt-BR";
      this.recognition.maxAlternatives = 3;

      this.recognition.onstart = () => {
        this.recognitionActive = true;
        this.isRecording = true;
        this.isPaused = false;
        this.lastSpeechTime = Date.now();
      };

      this.recognition.onresult = (event) => {
        // Ouviu: a conexão voltou (ou nunca caiu).
        this.reconnectAttempts = 0;
        let interim = "";
        let finalText = "";
        const currentTime = Date.now();

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          let bestTranscript = result[0].transcript;
          let bestConfidence = result[0].confidence || 0.5;

          for (let j = 1; j < result.length && j < 3; j++) {
            if (result[j].confidence > bestConfidence) {
              bestTranscript = result[j].transcript;
              bestConfidence = result[j].confidence;
            }
          }

          if (result.isFinal) {
            // salvia-860: o gatilho e procurado por TRECHO, nao no texto todo.
            // Cada `isFinal` corresponde a uma pausa de fala, e e exatamente a
            // unidade em que um comando e dito — procurar no acumulado casaria
            // "Salvia" ditos ha dez minutos.
            this.captureVoiceCommand(bestTranscript);

            finalText += bestTranscript + " ";
            this.lastSpeechTime = currentTime;
          } else {
            interim += bestTranscript;
          }
        }

        if (finalText || interim) {
          this.silentAttempts = 0;
          this.silenceWarned = false;
        }

        if (finalText) {
          this.finalTranscript += finalText;
          this.$emit("update:transcription", this.finalTranscript);
        }

        // O trecho ainda em reconhecimento vai para a caixa em cinza: sem ele, a
        // tela só muda quando o navegador fecha a frase numa pausa da fala, e
        // quem fala sem parar acha que nada está sendo ouvido.
        this.setInterim(interim);
      };

      this.recognition.onerror = (event) => {
        this.recognitionActive = false;

        switch (event.error) {
          case "network":
            // O reconhecimento do Chrome roda nos servidores do Google: sem
            // rede, ele para. Antes o médico tinha de pausar e retomar — e, sem
            // perceber o aviso, a consulta seguia sem transcrição.
            this.networkError = true;
            this.setInterim("");
            this.scheduleReconnect();
            return;
          case "not-allowed":
            this.error("Permissão de microfone negada");
            this.stopRecording();
            return;
          case "aborted":
            return;
          case "no-speech":
          case "audio-capture":
            // Microfone sem som ou ausente — p.ex. MacBook com a tampa fechada,
            // que desliga o microfone interno. Antes isto tentava de novo para
            // sempre, mudo (no-speech) ou com um toast a cada segundo
            // (audio-capture), e a caixa seguia vazia sem explicação.
            this.silentAttempts++;
            if (this.silentAttempts >= 3 && !this.silenceWarned) {
              this.silenceWarned = true;
              this.warning(
                "Não estou ouvindo nada — confira o microfone (com a tampa do Mac fechada, o interno fica desligado)",
              );
            }
            break;
          default:
            // service-not-allowed, language-not-supported…: tentar de novo não
            // resolve. Antes caíam no reinício silencioso e o botão seguia
            // "gravando" sem transcrever nada.
            this.error(
              `A transcrição não está disponível neste navegador (${event.error})`,
            );
            this.stopRecording();
            return;
        }

        if (this.isRecording && !this.isPaused && !this.networkError) {
          setTimeout(() => this.safeStartRecognition(), 1000);
        }
      };

      this.recognition.onend = () => {
        this.recognitionActive = false;
        if (this.isRecording && !this.isPaused && !this.networkError) {
          setTimeout(() => this.safeStartRecognition(), 100);
        }
      };
    },

    /**
     * Religa o reconhecedor depois de erro de rede, com espera crescente
     * (2s, 5s, 10s e depois a cada 20s) enquanto a gravação estiver ativa.
     * Avisa uma vez, na 3ª tentativa, para o médico saber que pode usar as
     * anotações enquanto isso.
     */
    scheduleReconnect() {
      clearTimeout(this.reconnectTimer);
      if (!this.isRecording || this.isPaused) return;

      const delays = [2000, 5000, 10000, 20000];
      const delay = delays[Math.min(this.reconnectAttempts, delays.length - 1)];
      this.reconnectAttempts++;
      if (this.reconnectAttempts === 3) {
        this.warning(
          "Sem conexão com a transcrição — tentando de novo sozinho. As anotações continuam funcionando.",
        );
      }

      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        if (!this.isRecording || this.isPaused) return;
        this.networkError = false;
        this.safeStartRecognition();
      }, delay);
    },

    /** O trecho ainda não fechado pelo reconhecedor — só exibição, nunca é gravado. */
    setInterim(text) {
      if (this.interimTranscript === text) return;
      this.interimTranscript = text;
      this.$emit("update:interimTranscript", text);
    },

    safeStartRecognition() {
      if (!this.recognition || this.recognitionActive || this.networkError)
        return;
      try {
        this.recognition.start();
      } catch (e) {
        if (e.message.includes("already started")) {
          this.recognitionActive = true;
        }
      }
    },

    cleanupRecognition() {
      if (this.recognition) {
        try {
          this.recognition.stop();
        } catch (e) {}
        this.recognitionActive = false;
      }
    },

    startRecording() {
      if (this.isPaused) {
        this.isPaused = false;
        // Retomar é a saída do erro de rede: sem zerar a flag, o
        // safeStartRecognition desistia e a gravação seguia sem transcrever.
        this.networkError = false;
        this.reconnectAttempts = 0;
        this.silentAttempts = 0;
        this.silenceWarned = false;
        this.safeStartRecognition();
        this.startRecordingInterval();
        this.success("Gravação retomada");
        this.timerInterval = setInterval(() => {
          this.recordingTime++;
          this.$emit("update:recordingTime", this.recordingTime);
        }, 1000);
      } else if (!this.isRecording) {
        /*
          salvia-867 — gravar de novo CONTINUA, nao sobrescreve.

          Antes, esta linha era `this.finalTranscript = ""`. Comecar uma gravacao
          nova no mesmo atendimento zerava o acumulador e o
          `update:transcription` seguinte trocava o valor inteiro no
          AppointmentDialog: a consulta anterior sumia da tela sem UMA pergunta.
          Aconteceu em stage em 15/09, e o material perdido e a consulta inteira
          — que nao se reconstroi, porque a paciente ja foi embora.

          Continuar e o padrao (decisao do Ariel, 16/09) porque e o que "gravar de
          novo" costuma significar para quem esta atendendo: a consulta foi
          interrompida e esta sendo retomada. Substituir de proposito continua
          possivel — o medico apaga pelo lapis de edicao, que e uma acao dele,
          explicita e reversivel ate salvar.

          O que emendamos e so uma linha em branco. NENHUM texto inventado entra
          aqui: isto vira prontuario e alimenta o agente do resumo, e palavra que
          ninguem disse nao pode nascer de um separador.
        */
        const previousTranscript = String(this.initialTranscript || "").trim();
        const resuming = previousTranscript.length > 0;

        // Retomando, o inicio que vale e o da PRIMEIRA gravacao: o horario que o
        // atendimento comecou, nao o da interrupcao.
        if (!resuming || !this.startTime) {
          const now = new Date();
          this.startTime = `${now.getHours().toString().padStart(2, "0")}:${now
            .getMinutes()
            .toString()
            .padStart(2, "0")}:${now.getSeconds().toString().padStart(2, "0")}`;
        }

        this.finalTranscript = resuming ? `${previousTranscript}\n\n` : "";
        this.setInterim("");
        this.recordingTime = 0;
        this.networkError = false;
        this.reconnectAttempts = 0;
        this.silentAttempts = 0;
        this.silenceWarned = false;
        this.lastSpeechTime = Date.now();
        this.endTime = null; // Limpar endTime anterior

        if (this.recognition) {
          this.safeStartRecognition();
          this.startRecordingInterval();
        }

        this.isRecording = true;
        this.success(
          resuming
            ? "Continuando a gravação desta consulta"
            : "Gravação iniciada!",
        );

        this.timerInterval = setInterval(() => {
          this.recordingTime++;
          this.$emit("update:recordingTime", this.recordingTime);
        }, 1000);
      }
    },

    pauseRecording() {
      clearTimeout(this.reconnectTimer);
      if (this.recognition && this.isRecording && this.recognitionActive) {
        try {
          this.recognition.stop();
          this.recognitionActive = false;
        } catch (e) {}
      }

      this.isPaused = true;
      this.setInterim("");
      clearInterval(this.timerInterval);
      clearInterval(this.recordingInterval);
      this.warning("Gravação pausada");

      // Emitir transcrição acumulada para gerar resumo parcial
      this.$emit("recording-paused", {
        transcription: this.finalTranscript,
      });
    },

    stopRecording() {
      clearTimeout(this.reconnectTimer);
      this.isRecording = false;
      this.isPaused = false;
      this.setInterim("");

      this.cleanupRecognition();
      clearInterval(this.timerInterval);
      clearInterval(this.recordingInterval);

      // NOVO: Capturar horário de fim
      const now = new Date();
      this.endTime = `${now.getHours().toString().padStart(2, "0")}:${now
        .getMinutes()
        .toString()
        .padStart(2, "0")}:${now.getSeconds().toString().padStart(2, "0")}`;

      const recordingEndTime = now.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });

      this.recordingTime = 0;
      this.$emit("update:recordingTime", 0);

      this.info("Gravação finalizada!");

      // NOVO: Emitir startTime e endTime junto
      this.$emit("recording-stopped", {
        transcription: this.finalTranscript,
        endTime: recordingEndTime,
        startTime: this.startTime, // HH:MM:SS
        endTimeFormatted: this.endTime, // HH:MM:SS
      });
    },

    startRecordingInterval() {
      clearInterval(this.recordingInterval);
      if (!this.networkError) {
        this.recordingInterval = setInterval(() => {
          if (
            this.isRecording &&
            !this.isPaused &&
            !this.networkError &&
            this.recognitionActive
          ) {
            try {
              this.recognition.stop();
            } catch (e) {}
          }
        }, 30000);
      }
    },
  },
};
</script>

<style scoped>
/* Enquanto grava, o circulo fica `disabled` — nao ha o que clicar, so da para
   pausar. Mas o Vuetify pinta TODO botao desabilitado de cinza, entao o
   vermelho sumia justamente no unico instante em que ele precisa aparecer.
   Aqui `disabled` nao quer dizer "indisponivel", quer dizer "gravando": este
   circulo E o indicador de estado.

   A classe repetida NAO e engano. A regra que estamos vencendo e
   `.theme--light.v-btn.v-btn--disabled.v-btn--has-bg` (vuetify.css:18292),
   quatro classes e `!important`; com menos que isto o cinza volta. Sem citar
   `.theme--*` de proposito, para valer nos dois temas.

   O hex repete o `error` de src/plugins/vuetify.js:25 porque o projeto nao
   liga as custom properties do Vuetify — nao existe `--v-error-base` aqui. */
.v-btn.btn-rec.btn-rec.v-btn--disabled {
  background-color: #ff5252 !important;
}
.btn-rec.btn-rec.v-btn--disabled .v-icon {
  color: #fff !important;
}

/* Pedido de 24/09, reforcado no mesmo dia (o primeiro pulso passava
   despercebido sem focar o olhar no botao). O escurecimento e via `filter`,
   NAO `background-color`: o botao fica disabled enquanto pulsa, e a regra
   logo acima que mantem o vermelho no disabled usa `!important` —
   `!important` sempre vence animacao de CSS, entao um `background-color`
   animado aqui simplesmente nao apareceria. `filter` e outra propriedade,
   fora dessa disputa, e de quebra escurece o icone branco junto — o botao
   INTEIRO pulsa mais escuro, nao so o fundo. */
.btn-rec--pulsando {
  animation: rec-pulso 1.2s ease-in-out infinite;
}
@keyframes rec-pulso {
  0%,
  100% {
    transform: scale(1);
    filter: brightness(1);
  }
  50% {
    transform: scale(1.1);
    filter: brightness(0.78);
  }
}

/* salvia-860: a lista de comandos nao pode competir com o indicador de
   gravacao — ela informa, nao alarma. */
.cv-lista {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  max-width: 460px;
}
.cv-titulo {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  color: var(--ds-text-muted, rgba(0, 0, 0, 0.6));
  margin-right: 4px;
  white-space: nowrap;
}
.cv-chip {
  max-width: 260px;
}

/* Pedido de 24/09: as bolinhas sobem e descem em onda (uma apos a outra) em
   vez de so piscar. O `animation-delay` escalonado por bolinha e o que faz
   ler como onda — as tres com o mesmo delay pulsariam juntas, sem sensacao
   de movimento passando de uma pra outra. */
.recording-dot {
  width: 8px;
  height: 8px;
  background-color: var(--ds-red);
  border-radius: 50%;
  animation: rec-onda 1s ease-in-out infinite;
}
.recording-dot:nth-child(2) {
  animation-delay: 0.15s;
}
.recording-dot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes rec-onda {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .btn-rec--pulsando {
    animation: none;
  }
  .recording-dot {
    animation: none;
  }
}
</style>
